const BETA_FIELDS = ['beta_method', 'beta_observation_count', 'beta_benchmark_symbol',
    'beta_asof_date', 'beta_source_symbol', 'beta_updated_at'];
const ADTV_FIELDS = ['adtv_method', 'adtv_observation_count', 'adtv_reported_window', 'adtv_asof_date',
    'adtv_source_symbol', 'adtv_quality', 'adtv_window_start', 'adtv_window_end', 'adtv_updated_at'];

export function selectMarketMetric(row, liveQuote, metric, preferLive) {
    const primary = preferLive ? liveQuote : row;
    const secondary = preferLive ? row : liveQuote;
    const source = primary?.[metric] != null ? primary : secondary || {};
    const fields = metric === 'beta' ? BETA_FIELDS : ADTV_FIELDS;
    // Replace the whole observation. A raw quote must not inherit the previous
    // value's method, sample count or provenance when metadata is absent.
    return { [metric]: source?.[metric] ?? null,
        ...Object.fromEntries(fields.map((field) => [field, source?.[field] ?? null])) };
}

function sampleLabel(count, noun) {
    return Number.isSafeInteger(count) && count > 0
        ? `${count} ${noun}${count === 1 ? '' : 's'}`
        : noun === 'daily return' ? 'daily returns' : 'trading days';
}

export function marketMetricQualifier(row, metric) {
    if (metric === 'beta') {
        return { short_history_ols: 'Short', yahoo_reported: 'Yahoo' }[row.beta_method] || '';
    }
    return { short_history_three_month_mean: 'Short', yahoo_reported: 'Yahoo' }[row.adtv_method] || '';
}

export function marketMetricExplanation(row, metric) {
    if (metric === 'beta') {
        const sample = sampleLabel(row.beta_observation_count, 'daily return');
        const benchmark = row.beta_benchmark_symbol ? ` against ${row.beta_benchmark_symbol}` : '';
        switch (row.beta_method) {
        case 'short_history_ols': return `Short-history beta estimate · ${sample}${benchmark}.`;
        case 'daily_ols': return `Calculated beta · ${sample}${benchmark}.`;
        case 'yahoo_reported': return 'Yahoo-reported beta. Yahoo does not provide its calculation window or benchmark in this response.';
        default: return '';
        }
    }
    const sample = sampleLabel(row.adtv_observation_count, 'trading day');
    const computedWindow = row.adtv_window_start && row.adtv_window_end
        ? ` (${row.adtv_window_start} to ${row.adtv_window_end})` : '';
    switch (row.adtv_method) {
    case 'short_history_three_month_mean': return `Average daily volume over ${sample} in the three-month window${computedWindow}; limited history.`;
    case 'three_month_daily_mean': return `Average daily volume over ${sample} in the three-month window${computedWindow}.`;
    case 'yahoo_reported': {
        const window = row.adtv_reported_window === '3_month' ? '3-month' : null;
        return `Yahoo-reported average volume${window ? ` (${window} field)` : ' (window not supplied)'}; the actual sample count is not supplied.`;
    }
    default: return '';
    }
}


export function marketDataUpdateTooltip(value) {
    if (!value) return 'Update time not recorded.';
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return 'Update time not recorded.';
    return `Updated ${date.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
    })}.`;
}
